// Client-side conversion funnel tracker.
// Insert is allowed for anon/authenticated via RLS; admins read aggregates.
// Fire-and-forget — never blocks UI.
// Gated by cookie consent: if the user has not granted analytics consent
// (CookieBanner / privacy settings), nothing is sent.

import { supabase } from "@/integrations/supabase/client";
import { hasAnalyticsConsent } from "@/lib/consent";
import { getAllAssignments } from "@/lib/ab";

const SESSION_KEY = "peoples:funnel:sid";

export const FUNNEL_EVENTS = [
  "hero_cta_click",
  "auth_view",
  "auth_signup_success",
  "auth_signin_success",
  "post_job_view",
  "post_job_step_service",
  "post_job_step_details",
  "post_job_step_location",
  "post_job_abandon",
  "post_job_submit",
  "post_job_ai_generate",
  "post_job_ai_applied",
  "checkout_view",
  "checkout_started",
  "booking_paid",
  // Homepage social-proof attribution
  "trust_strip_impression",
  "trust_strip_click",
  "safety_modal_view",
  "safety_modal_cta_click",
  "testimonial_impression",
  "testimonial_click",
  "top_rated_impression",
  "top_rated_click",
  "postcode_invalid",
  // Helper profile attribution
  "helper_profile_view",
  "helper_book_click",
  "helper_message_click",
  // Job lifecycle / completion engine
  "offer_view",
  "offer_accept",
  "offer_decline",
  "booking_started",
  "booking_completed",
  "review_prompt_view",
  "review_submit",
  // Our Journey / Our Ambition (vision section)
  "journey_impression",
  "journey_scroll_50",
  "journey_scroll_100",
  "ambition_impression",
  "ambition_logos_click",
  "ambition_disclaimer_click",
] as const;
export type FunnelEvent = (typeof FUNNEL_EVENTS)[number];

function getSessionId(): string {
  if (typeof window === "undefined") return "ssr-noop-session";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `s_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function readUtm(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  if (typeof window === "undefined") return {};
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") ?? undefined,
      utm_medium: p.get("utm_medium") ?? undefined,
      utm_campaign: p.get("utm_campaign") ?? undefined,
    };
  } catch { return {}; }
}

export async function track(event: FunnelEvent, props: Record<string, unknown> = {}): Promise<void> {
  if (typeof window === "undefined") return;
  // Respect cookie consent — opt-in only, core functionality unaffected.
  if (!hasAnalyticsConsent()) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("funnel_events").insert({
      event,
      session_id: getSessionId(),
      user_id: user?.id ?? null,
      path: window.location.pathname,
      referrer: document.referrer || null,
      ...readUtm(),
      props: { ...props, ab: getAllAssignments() } as never,
    });
  } catch {
    // Never break UX on analytics failure.
  }
}
